import { type GetFlexiBenefitsSettingsResponse } from 'api/flexibleBenefits';
import { DisplaySetting, type FlexiBenefitsSettings } from './types';
import { uniqueId } from 'utils/Numbers';
import { format, parse } from 'date-fns';
import { dateFormats } from 'utils/Dates';
import { GenericDeclaration } from 'components/ui/DeclarationWindow/types';
import {
  BulkSettingUpdateItemInput,
  EffectiveSetting,
} from 'api/gql-utils/flexibleBenefits/flexibleBenefitsSettings/types';

export const parseSettings = (
  settings: GetFlexiBenefitsSettingsResponse,
): FlexiBenefitsSettings => {
  const { employeeDeclarationWindow, employeeProofUploadWindow, ...rest } = settings;
  const parsed = { ...rest } as FlexiBenefitsSettings;
  const declarationWindow = { ...employeeDeclarationWindow } as GenericDeclaration;
  if (declarationWindow.type === 'custom') {
    if (employeeDeclarationWindow.dates && Array.isArray(employeeDeclarationWindow.dates)) {
      let dates = employeeDeclarationWindow.dates || [];
      const parsedDates = dates.reduce(
        (a, c) => ({
          ...a,
          [uniqueId()]: {
            startDate: parse(c.startDate, dateFormats.dayMonth, new Date()),
            endDate: parse(c.endDate, dateFormats.dayMonth, new Date()),
          },
        }),
        {},
      );
      declarationWindow.dates = parsedDates;
    }
  }

  const proofWindow = { ...employeeProofUploadWindow } as GenericDeclaration;
  if (proofWindow.type === 'custom') {
    if (employeeProofUploadWindow.dates && Array.isArray(employeeProofUploadWindow.dates)) {
      let dates = employeeProofUploadWindow.dates || [];
      const parsedDates = dates.reduce(
        (a, c) => ({
          ...a,
          [uniqueId()]: {
            startDate: parse(c.startDate, dateFormats.dayMonth, new Date()),
            endDate: parse(c.endDate, dateFormats.dayMonth, new Date()),
          },
        }),
        {},
      );
      proofWindow.dates = parsedDates;
    }
  }
  parsed.employeeDeclarationWindow = declarationWindow;
  parsed.employeeProofUploadWindow = proofWindow;
  parsed.isTaxableAfterMonthEnabled = !!parsed.fbpDefaultTaxableSettingAutoTurnOn;
  parsed.isAutoReleaseEnabledForUnclaimedBenefits =
    !!parsed.shouldHoldFlexiAmountSettingAutoTurnOff;

  parsed.isAutoOpenWindowForNewEmployeesEnabled = !!parsed.autoOpenDeclarationWindowForNewEmployees;
  parsed.isAutoOpenWindowForSalaryRevisedEmployeesEnabled =
    !!parsed.autoOpenDeclarationWindowForSalaryRevisedEmployees;

  return parsed as FlexiBenefitsSettings;
};

export const createPayload = (
  settings: FlexiBenefitsSettings,
): Partial<GetFlexiBenefitsSettingsResponse> => {
  const {
    employeeDeclarationWindow,
    employeeProofUploadWindow,
    isAutoReleaseEnabledForUnclaimedBenefits,
    isTaxableAfterMonthEnabled,
    isAutoOpenWindowForNewEmployeesEnabled,
    isAutoOpenWindowForSalaryRevisedEmployeesEnabled,
    ...rest
  } = settings;
  const payload = { ...rest } as Partial<GetFlexiBenefitsSettingsResponse>;

  const declarationWindow = {
    ...employeeDeclarationWindow,
  } as GetFlexiBenefitsSettingsResponse['employeeDeclarationWindow'];
  if (declarationWindow.type === 'custom' && employeeDeclarationWindow.type === 'custom') {
    const dates = employeeDeclarationWindow.dates || {};
    const parsedDates = Object.values(dates).map((e) => ({
      ...e,
      startDate: format(e.startDate || new Date(), dateFormats.dayMonth),
      endDate: format(e.endDate || new Date(), dateFormats.dayMonth),
    }));
    declarationWindow.dates = parsedDates;
  }

  const proofWindow = {
    ...employeeProofUploadWindow,
  } as GetFlexiBenefitsSettingsResponse['employeeDeclarationWindow'];
  if (proofWindow.type === 'custom' && employeeProofUploadWindow.type === 'custom') {
    const dates = employeeProofUploadWindow.dates || {};
    const parsedDates = Object.values(dates).map((e) => ({
      ...e,
      startDate: format(e.startDate || new Date(), dateFormats.dayMonth),
      endDate: format(e.endDate || new Date(), dateFormats.dayMonth),
    }));
    proofWindow.dates = parsedDates;
  }

  payload.employeeDeclarationWindow = declarationWindow;
  payload.employeeProofUploadWindow = proofWindow;

  payload.fbpDefaultTaxableSettingAutoTurnOn = isTaxableAfterMonthEnabled
    ? payload.fbpDefaultTaxableSettingAutoTurnOn
    : null;
  payload.shouldHoldFlexiAmountSettingAutoTurnOff = isAutoReleaseEnabledForUnclaimedBenefits
    ? payload.shouldHoldFlexiAmountSettingAutoTurnOff
    : null;

  payload.autoOpenDeclarationWindowForNewEmployees =
    isAutoOpenWindowForNewEmployeesEnabled && payload.autoOpenDeclarationWindowForNewEmployees
      ? payload.autoOpenDeclarationWindowForNewEmployees
      : null;
  payload.autoOpenDeclarationWindowForSalaryRevisedEmployees =
    isAutoOpenWindowForSalaryRevisedEmployeesEnabled &&
    !!payload.autoOpenDeclarationWindowForSalaryRevisedEmployees
      ? payload.autoOpenDeclarationWindowForSalaryRevisedEmployees
      : null;

  return payload;
};

export const transformBulkFlexiSettingsPayload = (settings: FlexiBenefitsSettings) => {
  const bulkSettingsPayload = [
    {
      setting_name: 'consider_fbp_taxable_by_default',
      boolean_value: {
        value: settings.FbpDefaultTaxable,
      },
    },
  ];

  return {
    updates: bulkSettingsPayload,
  };
};

type FBPSettingsConfig = {
  field: keyof FlexiBenefitsSettings;
  valueType: 'boolean' | 'string' | 'number';
  transform?: (value: any) => any;
};

const FBP_SETTING_FIELD_MAP: Record<string, FBPSettingsConfig> = {
  consider_fbp_taxable_by_default: {
    field: 'FbpDefaultTaxable',
    valueType: 'boolean',
  },
  auto_open_declaration_for_new: {
    field: 'isAutoOpenWindowForNewEmployeesEnabled',
    valueType: 'boolean',
  },
  auto_open_declaration_for_revised: {
    field: 'isAutoOpenWindowForSalaryRevisedEmployeesEnabled',
    valueType: 'boolean',
  },
  upload_proofs_until_dismissal: {
    field: 'allowProofsUploadTillDismissal',
    valueType: 'boolean',
  },
  declaration_window_duration_for_new_employees: {
    field: 'autoOpenDeclarationWindowForNewEmployees',
    valueType: 'number',
    transform: (value: number) => String(value),
  },
  declaration_window_duration_for_revised_employees: {
    field: 'autoOpenDeclarationWindowForSalaryRevisedEmployees',
    valueType: 'number',
    transform: (value: number) => String(value),
  },
};

const getValueKeyForType = (
  valueType: 'boolean' | 'string' | 'number',
): 'boolean_value' | 'string_value' | 'number_value' => {
  const valueKeyMap = {
    boolean: 'boolean_value',
    string: 'string_value',
    number: 'number_value',
  } as const;
  return valueKeyMap[valueType];
};

const parseWindowConfig = (
  mapValue: any,
  fieldName: 'employeeDeclarationWindow' | 'employeeProofUploadWindow',
  parsed: Partial<FlexiBenefitsSettings>,
) => {
  if (!mapValue) return;

  const { active_key, values } = mapValue;

  if (active_key === 'always_open') {
    parsed[fieldName] = { type: 'always' };
    return;
  }

  if (active_key === 'every_month_at_period') {
    const range = values?.every_month_at_period?.number_range_value?.value;
    if (range) {
      parsed[fieldName] = {
        type: 'monthly',
        startDate: range.start,
        endDate: range.end,
      };
    }
    return;
  }

  if (active_key === 'custom_range') {
    const dateRanges = values?.custom_range?.date_ranges_value?.values;
    if (dateRanges) {
      const dates = dateRanges.reduce(
        (acc: Record<string, { startDate: Date; endDate: Date }>, range: any) => ({
          ...acc,
          [uniqueId()]: {
            startDate: new Date(range.start * 1000),
            endDate: new Date(range.end * 1000),
          },
        }),
        {},
      );
      parsed[fieldName] = { type: 'custom', dates };
    }
  }
};

const parseTaxableMonth = (
  monthValue?: number | null,
): Partial<FlexiBenefitsSettings> => {
  const defaultMonth = '03';

  if (!monthValue || monthValue === 3) {
    return {
      isTaxableAfterMonthEnabled: false,
      fbpDefaultTaxableSettingAutoTurnOn: defaultMonth,
    };
  }

  return {
    isTaxableAfterMonthEnabled: true,
    fbpDefaultTaxableSettingAutoTurnOn: monthValue.toString().padStart(2, '0'),
  };
};

export const parseListEffectiveSettings = (
  settings: EffectiveSetting[],
): Partial<FlexiBenefitsSettings> => {
  const parsed: Partial<FlexiBenefitsSettings> = {};

  settings.forEach((setting) => {
    const settingConfig = FBP_SETTING_FIELD_MAP[setting.setting_name];

    if (settingConfig) {
      const valueKey = getValueKeyForType(settingConfig.valueType);
      const settingValue = setting[valueKey];

      if (settingValue !== null && settingValue !== undefined) {
        const value = settingConfig.transform
          ? settingConfig.transform(settingValue.value)
          : settingValue.value;
        (parsed as any)[settingConfig.field] = value;
      }
      return;
    }

    switch (setting.setting_name) {
      case 'declaration_window_config':
        parseWindowConfig(setting.map_value, 'employeeDeclarationWindow', parsed);
        break;

      case 'proof_upload_window_config':
        parseWindowConfig(setting.map_value, 'employeeProofUploadWindow', parsed);
        break;

      case 'fbp_taxable_from_a_certain_month':
        Object.assign(parsed, parseTaxableMonth(setting.number_value?.value));
        break;
    }
  });

  return parsed;
};

type TransformConfig = {
  settingName: string;
  field: keyof FlexiBenefitsSettings;
  valueType: 'boolean' | 'string' | 'number';
  transform?: (value: any) => any;
};

const TRANSFORM_FLEXI_SETTING_MAP: Record<string, TransformConfig> = {
  consider_fbp_taxable_by_default: {
    settingName: 'consider_fbp_taxable_by_default',
    field: 'FbpDefaultTaxable',
    valueType: 'boolean',
  },
  auto_open_declaration_for_new: {
    settingName: 'auto_open_declaration_for_new',
    field: 'isAutoOpenWindowForNewEmployeesEnabled',
    valueType: 'boolean',
  },
  auto_open_declaration_for_revised: {
    settingName: 'auto_open_declaration_for_revised',
    field: 'isAutoOpenWindowForSalaryRevisedEmployeesEnabled',
    valueType: 'boolean',
  },
  upload_proofs_until_dismissal: {
    settingName: 'upload_proofs_until_dismissal',
    field: 'allowProofsUploadTillDismissal',
    valueType: 'boolean',
  },
  declaration_window_duration_for_new_employees: {
    settingName: 'declaration_window_duration_for_new_employees',
    field: 'autoOpenDeclarationWindowForNewEmployees',
    valueType: 'number',
    transform: (value: string) => parseInt(value, 10),
  },
  declaration_window_duration_for_revised_employees: {
    settingName: 'declaration_window_duration_for_revised_employees',
    field: 'autoOpenDeclarationWindowForSalaryRevisedEmployees',
    valueType: 'number',
    transform: (value: string) => parseInt(value, 10),
  },
};

const createUpdateItem = (
  settingName: string,
  valueType: 'boolean' | 'string' | 'number',
  value: any,
): BulkSettingUpdateItemInput => {
  const update: BulkSettingUpdateItemInput = { setting_name: settingName };

  if (valueType === 'boolean') {
    update.boolean_value = { value };
  } else if (valueType === 'string') {
    update.string_value = { value };
  } else if (valueType === 'number') {
    update.number_value = { value };
  }

  return update;
};

const transformTaxableMonth = (
  settings: FlexiBenefitsSettings,
): BulkSettingUpdateItemInput | null => {
  if (settings.isTaxableAfterMonthEnabled === undefined || settings.FbpDefaultTaxable !== false) {
    return null;
  }

  const monthValue =
    settings.isTaxableAfterMonthEnabled && settings.fbpDefaultTaxableSettingAutoTurnOn
      ? parseInt(settings.fbpDefaultTaxableSettingAutoTurnOn, 10)
      : 3;

  return {
    setting_name: 'fbp_taxable_from_a_certain_month',
    number_value: { value: monthValue },
  };
};

export const transformToBulkUpdateFormat = (
  settings: FlexiBenefitsSettings,
): BulkSettingUpdateItemInput[] => {
  const updates: BulkSettingUpdateItemInput[] = [];

  if (settings.employeeDeclarationWindow) {
    updates.push({
      setting_name: 'declaration_window_config',
      map_value_update: transformWindowToMapValue(settings.employeeDeclarationWindow),
    });
  }

  if (settings.employeeProofUploadWindow) {
    updates.push({
      setting_name: 'proof_upload_window_config',
      map_value_update: transformWindowToMapValue(settings.employeeProofUploadWindow),
    });
  }

  Object.values(TRANSFORM_FLEXI_SETTING_MAP).forEach((config) => {
    const value = settings[config.field];
    if (value === undefined || value === null) return;

    const transformedValue = config.transform ? config.transform(value) : value;
    updates.push(createUpdateItem(config.settingName, config.valueType, transformedValue));
  });

  const taxableMonthUpdate = transformTaxableMonth(settings);
  if (taxableMonthUpdate) {
    updates.push(taxableMonthUpdate);
  }

  return updates;
};

const convertToUnixTimestamp = (date: string | Date): number => {
  const dateString = typeof date === 'string' ? date : format(date, dateFormats.dayMonth);
  return Math.floor(parse(dateString, dateFormats.dayMonth, new Date()).getTime() / 1000);
};

const transformWindowToMapValue = (window: GenericDeclaration) => {
  if (window.type === 'always') {
    return {
      active_key: 'always_open',
      active_value: { boolean_value: { value: true } },
    };
  }

  if (window.type === 'monthly' && 'startDate' in window && 'endDate' in window) {
    return {
      active_key: 'every_month_at_period',
      active_value: {
        number_range_value: {
          value: {
            start: Number(window.startDate),
            end: Number(window.endDate),
          },
        },
      },
    };
  }

  if (window.type === 'custom' && window.dates && typeof window.dates === 'object') {
    const dateRanges = Object.values(window.dates).map((date) => ({
      start: convertToUnixTimestamp(date.startDate || new Date()),
      end: convertToUnixTimestamp(date.endDate || new Date()),
    }));

    return {
      active_key: 'custom_range',
      active_value: {
        date_ranges_value: { values: dateRanges },
      },
    };
  }

  return {
    active_key: 'always_open',
    active_value: { boolean_value: { value: true } },
  };
};

export const mapNewFlowSettingsToDisplay = (settings: EffectiveSetting[]): DisplaySetting[] => {
  const settingsMap: Record<string, string> = {};

  settings.forEach((setting) => {
    const name = setting.setting_name || '';
    switch (name) {
      case 'consider_fbp_taxable_by_default':
        settingsMap['FBP default taxable'] = setting.boolean_value?.value ? 'Yes' : 'No';
        break;
      case 'auto_open_declaration_for_new':
        settingsMap['Auto-open window for new employees'] = setting.boolean_value?.value
          ? 'Yes'
          : 'No';
        break;
      case 'auto_open_declaration_for_revised':
        settingsMap['Auto-open window for salary-revised employees'] = setting.boolean_value?.value
          ? 'Yes'
          : 'No';
        break;
      case 'upload_proofs_until_dismissal':
        settingsMap['Allow proofs upload till dismissal'] = setting.boolean_value?.value
          ? 'Yes'
          : 'No';
        break;
    }
  });

  return Object.entries(settingsMap).map(([label, value]) => ({
    label,
    value,
  }));
};

export const formatWindowConfig = (window: any): string => {
  if (!window) return '-';

  if (window.type === 'always') {
    return 'Always Open';
  }

  if (window.type === 'monthly' && window.startDate && window.endDate) {
    return `Every month from ${window.startDate} to ${window.endDate}`;
  }

  if (window.type === 'custom' && window.dates) {
    const dateCount = Object.keys(window.dates).length;
    return `Custom Range (${dateCount} period${dateCount > 1 ? 's' : ''})`;
  }

  return '-';
};

export const mapOldFlowSettingsToDisplay = (data: any): DisplaySetting[] => {
  const parsedSettings = parseSettings(data);

  return [
    {
      label: 'Employees can declare FBP',
      value: parsedSettings.employeeCanDeclareFlexiBenefits ? 'Yes' : 'No',
    },
    {
      label: 'Hold unclaimed FBP',
      value: parsedSettings.shouldHoldFlexiAmount ? 'Yes' : 'No',
    },
    {
      label: 'FBP declaration window',
      value: formatWindowConfig(parsedSettings.employeeDeclarationWindow),
    },
    {
      label: 'Proof upload window',
      value: formatWindowConfig(parsedSettings.employeeProofUploadWindow),
    },
  ];
};
